import { BaseNode, BaseNodeProps } from "./BaseNode";
import { NodeData } from "../types";
import ActivityIcon from "lucide-react/dist/esm/icons/activity";
import CheckCircle2Icon from "lucide-react/dist/esm/icons/check-circle-2";
import XCircleIcon from "lucide-react/dist/esm/icons/x-circle";
import Loader2Icon from "lucide-react/dist/esm/icons/loader-2";
import FlaskConicalIcon from "lucide-react/dist/esm/icons/flask-conical";
import { cn } from "@quake/shared-ui/lib/utils";
import quakelogo from "@assets/images/quake_logo.svg";
import { getLucideIcon, isUrl } from "../../workflow-builder/lucide-icon-map";
import { useMemo } from "react";

function NodeAppIcon({ app }: { app?: { id: string; name: string; logo_url?: string } }) {
  if (!app?.logo_url) return <ActivityIcon />;
  if (app.name.includes("Quake"))
    return <img src={quakelogo} alt={app.name} className="w-full h-full object-contain" />;

  // Check if it's a Lucide icon name (custom entities)
  const IconComponent = getLucideIcon(app.logo_url);
  if (IconComponent) return <IconComponent className="w-full h-full" />;

  if (isUrl(app.logo_url)) return <img src={app.logo_url} alt={app.name} className="w-full h-full object-contain" />;

  // Fallback: text/emoji
  return <span className="w-full h-full flex items-center justify-center text-sm">{app.logo_url}</span>;
}

interface ActionNodeProps extends BaseNodeProps {
  data: NodeData & {
    app?: {
      id: string;
      name: string;
      logo_url?: string;
    };
    output?: any;
    isExecuting?: boolean;
  };
  id: string;
}

export function ActionNode({
  data,
  onAddAction,
  onEdit,
  ...props
}: ActionNodeProps & { onAddAction?: () => void; onEdit?: () => void }) {
  // Check if mock data is configured
  const hasMockData = data.action?.mockOutput !== undefined &&
                      data.action?.mockOutput !== null &&
                      JSON.stringify(data.action.mockOutput) !== "{}";
  const isMockEnabled = data.action?.useMockOutput === true && hasMockData;

  const icon = useMemo(() => {
    if (data.action?.logo_url) {
      return <img src={data.action.logo_url} alt={data.app?.name || "Logo"} className="w-full h-full object-contain" />;
    }
    if (data.app?.logo_url) {
      return (
        <img
          src={data.app.name.includes("Quake") ? quakelogo : (data.app.logo_url ?? quakelogo)}
          alt={data.app.name}
          className="w-full h-full object-contain"
        />
      );
    }
    return <ActivityIcon />;
  }, [data.action?.logo_url, data.app?.name, data.app?.logo_url]);
  return (
    <BaseNode
      data={data}
      isSourceConnectable
      isTargetConnectable
      icon={<NodeAppIcon app={data.app} />}
      className={cn(
        "bg-white text-slate-800 border border-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-700",
        data.isExecuting && "border-blue-500",
        data.error && "border-red-500",
        data.output && !data.error && "border-green-500",
      )}
      onAddAction={onAddAction}
      onEdit={onEdit}
      {...props}
    >
      {data.isExecuting && (
        <div className="absolute -top-2 -right-2 bg-blue-500 rounded-full p-1">
          <Loader2Icon className="w-4 h-4 text-white motion-preset-spin" />
        </div>
      )}
      {data.error && (
        <div className="absolute -top-2 -right-2 bg-red-500 rounded-full p-1 cursor-pointer" onClick={onEdit}>
          <XCircleIcon className="w-4 h-4 text-white" />
        </div>
      )}
      {data.output !== undefined && !data.error && (
        <div className="absolute -top-2 -right-2 bg-green-500 rounded-full p-1 cursor-pointer" onClick={onEdit}>
          <CheckCircle2Icon className="w-4 h-4 text-white" />
        </div>
      )}
      {isMockEnabled && (
        <div
          className="absolute -top-2 -left-2 bg-amber-500 dark:bg-amber-600 rounded-full p-1 cursor-pointer"
          onClick={onEdit}
          title="Mock data enabled for testing"
        >
          <FlaskConicalIcon className="w-4 h-4 text-white" />
        </div>
      )}
    </BaseNode>
  );
}
